import { IFormController, IOnInit, IQService } from "angular";
import {
  Field,
  ProcessApiResponse,
  ProcessState,
  ProcessTrigger,
  ProcessTriggerCategory,
} from "../../model/ProcessApi";
import { ProcessGroup, ProcessInformation } from "../../model/ProcessGroups";
import { $confirm } from "../../../../@custom_types/confirm";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../../../records/server/serverUtility";
import { ServiceResponse } from "../../../records/server/query";
import {
  WorkItemService,
  vsServiceName as WorkItemServiceName,
} from "../../workItems/types";
import { IFieldConfigurationObject, IFieldGroup } from "AngularFormly";
import {
  UserProfile,
  vsName as UserProfileName,
} from "../../../application/UserProfile";
import { DateTime } from "luxon";

export class ProcessDetailController implements IOnInit {
  /*bindings*/
  process!: ProcessApiResponse<any>;
  processInformation!: ProcessInformation;
  processGroup!: ProcessGroup;
  /*bindings*/

  form?: IFormController;
  fields?: IFieldGroup[];
  fieldLookup?: Map<string, IFieldConfigurationObject>;

  displayStates: ProcessState[] = [];
  displayTriggers: ProcessTrigger[] = [];
  private currentState?: ProcessState;
  loading: boolean = false;

  noteCount: number = 0;

  static $inject = [
    "$confirm",
    "$q",
    ServerUtilityName,
    WorkItemServiceName,
    UserProfileName,
  ];
  constructor(
    private readonly $confirm: $confirm,
    private readonly $q: IQService,
    private readonly serverUtility: ServerUtility,
    private readonly workItemService: WorkItemService,
    private readonly userProfile: UserProfile,
  ) {}

  $onInit(): void {
    this.initializeFromServerResponse(this.process);
    this.refreshNoteCount();
  }

  private refreshNoteCount() {
    return this.serverUtility
      .query({
        url: "/api/processes/instances/notes",
        query: {
          ProcessInstancesId: this.process.Id,
          Take: 1,
        },
      })
      .then((queryResult) => (this.noteCount = queryResult.Total));
  }

  initializeFromServerResponse(process: ProcessApiResponse<any>) {
    const flatWorkflowItems = this.flattenFields(
      process.States.flatMap((s: ProcessState) => s.WorkflowItems ?? []),
    );
    this.parseDates(flatWorkflowItems);

    this.process = process;

    const oldStateName = this.currentState?.Name;
    const currentState = (this.currentState = this.process.States.find(
      (s) => s.Name === process.Status,
    ));

    if (!currentState) {
      return;
    }
    this.displayStates = this.getDisplayStates(currentState, process.States);
    this.displayTriggers = this.getDisplayTriggers(process);

    if (currentState.Name !== oldStateName) {
      this.initializeForm(currentState);
    } else {
      this.refreshForm(process, currentState);
    }
  }

  private parseDates(fields: Field[]) {
    fields.forEach((f) => {
      if (f.Type === "Date" && f.Value) {
        const value = DateTime.fromISO(f.Value).toJSDate();
        f.Value = value;
      }
    });
  }

  getDisplayStates(
    currentState: ProcessState,
    states: ProcessState[],
  ): ProcessState[] {
    const inProgressAndCompleteStates = states
      .filter(
        (s) =>
          s.ParentStateId === undefined &&
          (s.StatusCategory === "InProgress" ||
            s.StatusCategory === "Complete"),
      )
      .map((s) => ({
        ...s,
        Current: false,
        Active: false,
        Display: s.Name,
      }));

    const currentStates = this.getStatesToRoot(currentState).reverse(); //current states, top down
    const rootCurrentState = currentStates[0];
    const currentDisplayState = inProgressAndCompleteStates.find(
      (s) => s.Id === rootCurrentState.Id,
    )!;

    if (!currentDisplayState) {
      return inProgressAndCompleteStates;
    }

    currentDisplayState.Active = true;
    currentDisplayState.Current = true;
    currentDisplayState.Display = currentStates.map((s) => s.Name).join(" - ");

    return inProgressAndCompleteStates;
  }

  getDisplayTriggers(process: ProcessApiResponse<any>): ProcessTrigger[] {
    return process.Triggers.filter(
      (t) =>
        t.Permission == null ||
        t.Permission.trim() == "" ||
        this.userProfile.hasPermission(t.Permission),
    );
  }

  private refreshForm(process: ProcessApiResponse<any>, state: ProcessState) {
    const flatFields = this.flattenFields(state.WorkflowItems ?? []);
    flatFields.forEach((f) => {
      const formlyField = this.fieldLookup?.get(f.WorkItemId);
      if (formlyField) {
        this.workItemService.configureField(formlyField, f);
      }
      if (formlyField?.runExpressions) {
        formlyField?.runExpressions();
      }
    });

    this.displayTriggers = this.getDisplayTriggers(process);
  }

  private flattenFields(workflowItems: Field[]) {
    const result = [...workflowItems];
    const childrenWorkflowItems = result.flatMap((wi) => wi.Children ?? []);
    if (childrenWorkflowItems.length) {
      result.push(...this.flattenFields(childrenWorkflowItems));
    }

    return result;
  }

  private initializeForm(state: ProcessState) {
    const fieldInformation = this.workItemService.getFormFields(
      this.process,
      this.processGroup,
      state.WorkflowItems ?? [],
      (field: Field, commandName: string, commandArgument: any) =>
        this.fireWorkflowItemCommand(field, commandName, commandArgument),
    );

    this.fieldLookup = fieldInformation.fieldLookup;
    this.fields = this.workItemService.doLayout(fieldInformation.fields, 2);
  }

  stateSelected(state: ProcessState) {
    this.currentState = state;
    const activeStates = this.getStatesToRoot(state);
    this.displayStates = this.displayStates.map((ds) => ({
      ...ds,
      Active: activeStates.includes(ds),
    }));

    this.initializeForm(state);
  }

  private getStatesToRoot(state: ProcessState): ProcessState[] {
    const parentId = state.ParentStateId;
    if (parentId === undefined) {
      return [state];
    }
    const parentState = this.process.States.find((s) => s.Id === parentId);
    if (!parentState) throw new Error("Couldn't find parent state");
    return [state, ...this.getStatesToRoot(parentState)];
  }

  hasTrigger(name: string): boolean {
    return !!this.getTrigger(name);
  }

  fireWorkflowItemCommand(field: Field, commandName: string, argument: any) {
    this.loading = true;
    this.serverUtility
      .sendApiRequest<ServiceResponse<ProcessApiResponse<any>>>({
        method: "post",
        url: `api${this.processGroup.Url}/${this.process.Id}/items/${field.WorkItemId}/${commandName}`,
        data: { CommandArgument: argument },
      })
      .then((r) => {
        this.initializeFromServerResponse(r.Result);
      })
      .finally(() => {
        this.loading = false;
      });
  }

  private getTrigger(name: string): ProcessTrigger | undefined {
    return this.process.Triggers.find((t) => t.Name === name);
  }

  private getTriggers(category: ProcessTriggerCategory) {
    return this.process.Triggers.filter((t) => t.Category === category);
  }

  fireTrigger(name: string) {
    const trigger = this.getTrigger(name);
    const confirmationPromise = trigger?.ConfirmationText
      ? this.$confirm({ text: trigger.ConfirmationText })
      : this.$q.when();

    confirmationPromise
      .then(() => {
        return this.serverUtility.sendApiRequest<
          ServiceResponse<ProcessApiResponse<any>>
        >({
          method: "post",
          url: `/api${this.processGroup.Url}/${this.process.Id}/trigger`,
          params: {
            Name: name,
          },
        });
      })
      .then((r) => {
        this.initializeFromServerResponse(r.Result);
      });
  }
}
